import { FormEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shell } from '../components/Shell';
import { useAuth } from '../lib/auth';
import { createTenant } from '../lib/tenant';
import type { Region } from '../lib/defaults';

export function OnboardingSitePage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [domain, setDomain] = useState('');
  const [domainCount, setDomainCount] = useState<'1' | '2-5' | '6-25' | '25+'>('1');
  const [forWhom, setForWhom] = useState<'self' | 'client' | 'multiple'>('self');
  const [companyDomainsTotal, setCompanyDomainsTotal] = useState<'1-5' | '6-25' | '26-100' | '100+'>('1-5');
  const [region, setRegion] = useState<Region>('eu');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  if (loading) {
    return (
      <Shell centerLabel="Onboarding · 1 of 2">
        <div className="px-6 md:px-14 py-20 meta">Loading…</div>
      </Shell>
    );
  }
  if (!user) {
    return (
      <Shell centerLabel="Onboarding · 1 of 2">
        <section className="border-b" style={{ borderColor: 'var(--rule)' }}>
          <div className="max-w-[720px] mx-auto px-6 md:px-14 py-20">
            <div className="meta mb-3" style={{ color: 'var(--flag)' }}>Not signed in</div>
            <h1 className="font-medium" style={{ fontSize: '32px', letterSpacing: '-0.03em' }}>
              You need to sign in first.
            </h1>
            <a href="/signup" className="btn mt-6 inline-flex">
              <span>Send me a sign-in link</span> <span className="slash">/</span>
            </a>
          </div>
        </section>
      </Shell>
    );
  }

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!domain || submitting) return;
    setError('');
    setSubmitting(true);
    try {
      await createTenant({
        user,
        domain: domain.trim(),
        region,
        forWhom,
        domainCount,
        companyDomainsTotal,
      });
      navigate('/onboarding/branding');
    } catch (err) {
      const baseMsg = err instanceof Error ? err.message : 'Could not create your workspace';
      const code = (err as { code?: string })?.code;
      let claimsLine = '';
      try {
        const t = await user.getIdTokenResult();
        claimsLine = ` · email_verified=${t.claims.email_verified} · provider=${t.signInProvider} · email=${user.email}`;
      } catch {}
      setError(`${baseMsg}${code ? ` (${code})` : ''}${claimsLine}`);
      setSubmitting(false);
    }
  };

  return (
    <Shell centerLabel="Onboarding · 1 of 2">
      <section className="border-b" style={{ borderColor: 'var(--rule)' }}>
        <div className="max-w-[1100px] mx-auto px-6 md:px-14 py-14">
          <span className="pill mb-3 inline-flex">
            <span className="pill-dot" /> 03 · Your site · 60 seconds
          </span>
          <h1 className="font-medium mt-4 mb-3" style={{ fontSize: 'clamp(28px, 3.4vw, 44px)', lineHeight: 1.04, letterSpacing: '-0.03em' }}>
            Tell us where your banner <span className="grad-text">will live</span>.
          </h1>
          <p className="text-[15px] leading-[1.55] mb-10 max-w-[640px]" style={{ color: 'rgba(13,11,31,0.7)' }}>
            We&apos;ve prefilled what we can. Adjust if needed and continue. You can change all of this later in the dashboard.
          </p>

          <form onSubmit={onSubmit} className="max-w-[460px]">
            <label className="field">
              <span className="field-label">Primary domain</span>
              <input
                type="text"
                placeholder="yourdomain.com"
                required
                value={domain}
                onChange={(e) => setDomain(e.target.value)}
                disabled={submitting}
              />
              <span className="field-hint">The first site you&apos;ll install the banner on.</span>
            </label>

            <label className="field">
              <span className="field-label">How many domains do you want this CMP on?</span>
              <select value={domainCount} onChange={(e) => setDomainCount(e.target.value as '1' | '2-5' | '6-25' | '25+')} disabled={submitting}>
                <option value="1">1 domain</option>
                <option value="2-5">2 to 5 domains</option>
                <option value="6-25">6 to 25 domains</option>
                <option value="25+">25+ domains</option>
              </select>
              <span className="field-hint">Determines which plan suits you best. You can add more anytime.</span>
            </label>

            <label className="field">
              <span className="field-label">Who are you setting this up for?</span>
              <select value={forWhom} onChange={(e) => setForWhom(e.target.value as 'self' | 'client' | 'multiple')} disabled={submitting}>
                <option value="self">Myself / my own site</option>
                <option value="client">A client (I&apos;m a consultant or agency)</option>
                <option value="multiple">Multiple clients (agency workspace)</option>
              </select>
              <span className="field-hint">Used to suggest workspaces if you&apos;re managing multiple clients.</span>
            </label>

            <label className="field">
              <span className="field-label">How many domains does your company manage in total?</span>
              <select value={companyDomainsTotal} onChange={(e) => setCompanyDomainsTotal(e.target.value as '1-5' | '6-25' | '26-100' | '100+')} disabled={submitting}>
                <option value="1-5">1 to 5</option>
                <option value="6-25">6 to 25</option>
                <option value="26-100">26 to 100</option>
                <option value="100+">100+</option>
              </select>
              <span className="field-hint">Helps us recommend the right plan tier.</span>
            </label>

            <label className="field">
              <span className="field-label">Region preset</span>
              <select value={region} onChange={(e) => setRegion(e.target.value as Region)} disabled={submitting}>
                <option value="eu">EU · GDPR + ePrivacy</option>
                <option value="uk">UK · UK GDPR + PECR</option>
                <option value="us">US · CCPA / CPRA</option>
                <option value="il">Israel · PDPL</option>
              </select>
              <span className="field-hint">Sets the lawful basis, default categories, and supervisory authority. Lawyer-reviewed presets.</span>
            </label>

            {error && (
              <div
                className="mb-5 p-3 text-[13px]"
                style={{
                  background: 'rgba(232,81,122,0.1)',
                  border: '1px solid var(--flag)',
                  color: 'var(--flag)',
                  borderRadius: 'var(--radius)',
                }}
              >
                {error}
              </div>
            )}

            <button className="btn" type="submit" disabled={submitting}>
              <span>{submitting ? 'Creating workspace…' : 'Continue'}</span>
              {!submitting && <span className="slash">/</span>}
            </button>
          </form>
        </div>
      </section>
    </Shell>
  );
}
